import ap01Defaults from './ap01/commonComponents.ap01.defaults.json';
import ap02Defaults from './ap02/commonComponents.ap02.defaults.json';
import ap03Defaults from './ap03/commonComponents.ap03.defaults.json';

/**
 * Default props/content for AP01 and AP02 common components.
 *
 * Intended usage:
 * - Components import the relevant defaults and use them as fallback values.
 * - Strapi integration can map CMS data into the same prop shapes.
 *
 * Source files:
 * - AP01: src/components/common/defaults/ap01/commonComponents.ap01.defaults.json
 * - AP02: src/components/common/defaults/ap02/commonComponents.ap02.defaults.json
 */

// ─── AP01 raw defaults ────────────────────────────────────────────────────────

export const HERO_HEADER_DEFAULTS = ap01Defaults.heroHeader;
export const BILD_TEXT_DEFAULTS = ap01Defaults.bildText;
export const BILD_TEXT_DESIGN_DEFAULTS = ap01Defaults.bildTextDesign;
export const CONTENT_HEADER_DEFAULTS = ap01Defaults.contentHeader;
export const ARTICLE_PAGE_HEADER_DEFAULTS = ap01Defaults.articlePageHeader;
export const SITE_FOOTER_DEFAULTS = ap01Defaults.siteFooter;

// ─── AP01 typed defaults (narrows union-relevant literal fields) ───────────────

export const HERO_HEADER_DEFAULTS_TYPED = {
	...HERO_HEADER_DEFAULTS,
	variant: HERO_HEADER_DEFAULTS.variant as 'dark' | 'light',
	ctaVariant: HERO_HEADER_DEFAULTS.ctaVariant as 'circle' | 'underline' | 'arrow-slide'
} as const;

export const BILD_TEXT_DEFAULTS_TYPED = {
	...BILD_TEXT_DEFAULTS,
	imagePosition: BILD_TEXT_DEFAULTS.imagePosition as 'left' | 'right'
} as const;

export const BILD_TEXT_DESIGN_DEFAULTS_TYPED = {
	...BILD_TEXT_DESIGN_DEFAULTS,
	imagePosition: BILD_TEXT_DESIGN_DEFAULTS.imagePosition as 'left' | 'right'
} as const;

export const CONTENT_HEADER_DEFAULTS_TYPED = {
	...CONTENT_HEADER_DEFAULTS,
	body: CONTENT_HEADER_DEFAULTS.body as string | [string, string]
} as const;

export const ARTICLE_PAGE_HEADER_DEFAULTS_TYPED = {
	...ARTICLE_PAGE_HEADER_DEFAULTS,
	variant: ARTICLE_PAGE_HEADER_DEFAULTS.variant as 'dark' | 'light'
} as const;

export const SITE_FOOTER_DEFAULTS_TYPED = {
	...SITE_FOOTER_DEFAULTS,
	socialLinks: SITE_FOOTER_DEFAULTS.socialLinks as {
		key: string;
		iconAlt: string;
		href: string;
	}[]
};

// ─── AP02 raw defaults ────────────────────────────────────────────────────────

export const FILE_CAROUSEL_DEFAULTS = ap02Defaults.fileCarousel;
export const FILE_CARDS_GRID_DEFAULTS = ap02Defaults.fileCardsGrid;
export const SEARCH_SECTION_DEFAULTS = ap02Defaults.searchSection;
export const GRAPH_TABLE_SWITCH_DEFAULTS = ap02Defaults.graphTableSwitch;
export const EVENT_CARDS_GRID_DEFAULTS = ap02Defaults.eventCardsGrid;
export const EVENT_CARDS_DEFAULTS = ap02Defaults.eventCards;

// ─── AP02 typed defaults ──────────────────────────────────────────────────────

export const FILE_CAROUSEL_DEFAULTS_TYPED = {
	...FILE_CAROUSEL_DEFAULTS,
	items: FILE_CAROUSEL_DEFAULTS.items
} as const;

export const FILE_CARDS_GRID_DEFAULTS_TYPED = {
	availabilityLabelSuffix: FILE_CARDS_GRID_DEFAULTS.availabilityLabelSuffix,
	items: FILE_CARDS_GRID_DEFAULTS.items
} as const;

export const GRAPH_TABLE_SWITCH_DEFAULTS_TYPED = {
	...GRAPH_TABLE_SWITCH_DEFAULTS,
	initialView: GRAPH_TABLE_SWITCH_DEFAULTS.initialView as 'graph' | 'table',
	lineStyleMode: GRAPH_TABLE_SWITCH_DEFAULTS.lineStyleMode as 'mixed' | 'solid' | 'dashed'
} as const;

export const EVENT_CARDS_DEFAULTS_TYPED = {
	items: EVENT_CARDS_DEFAULTS
} as const;

// ─── AP03 raw defaults ────────────────────────────────────────────────────────

export const AP03_DASHBOARD_DEFAULTS = ap03Defaults.dashboard;
export const AP03_NAV_GRID_DEFAULTS = ap03Defaults.navGrid;
export const AP03_PARTNER_CARDS_DEFAULTS = ap03Defaults.partnerCards;
export const AP03_LOGIN_OVERLAY_DEFAULTS = ap03Defaults.loginOverlay;

// ─── AP03 typed defaults ──────────────────────────────────────────────────────

export const AP03_NAV_GRID_DEFAULTS_TYPED = {
	...AP03_NAV_GRID_DEFAULTS,
	columns: AP03_NAV_GRID_DEFAULTS.columns as 2 | 3 | 4,
	items: AP03_NAV_GRID_DEFAULTS.items as {
		id: string;
		headline: string;
		copy?: string;
		ctaLabel: string;
		href: string;
		imageUrl?: string;
		imageAlt?: string;
	}[]
} as const;

export const AP03_PARTNER_CARDS_DEFAULTS_TYPED = {
	...AP03_PARTNER_CARDS_DEFAULTS,
	items: AP03_PARTNER_CARDS_DEFAULTS.items as {
		id: string;
		headline: string;
		subline: string;
		logoSrc: string;
		logoAlt?: string;
		address?: string;
		phone?: string;
		email?: string;
		website?: string;
	}[]
} as const;
